import { AttachInternals, Component, Prop, h, Element, Listen, Method } from '@stencil/core';
import { generateUniqueId } from '../../utils';
import { Input, InputVariant } from '../../internal-components/input/Input';

// TODO: Not finished added only that i need for pp-datepicker.
@Component({
  tag: 'pp-input',
  styleUrl: 'pp-input.scss',
  shadow: true,
  formAssociated: true,
})
export class PpInput {
  @Element() element: HTMLElement;
  private readonly internalId = generateUniqueId();
  @AttachInternals() internals: ElementInternals;

  /**
   * Variant of the input. Uses Input variant type, and can have values 'outlined' | 'borderless'
   */
  @Prop() variant: InputVariant = 'outlined';

  /**
   * Name of the  input.
   */
  @Prop() name: string;

  /**
   * Reference to input element.
   */
  @Prop() inputRef: (elm?: HTMLInputElement) => void;

  /**
   * Input value
   */
  @Prop({ reflect: true, mutable: true }) value: string;

  /**
   * Makes the input disabled. This prevents users from being able to interact with the input.
   */
  @Prop() disabled: boolean = false;

  /**
   * Adds a unique identifier for the input. Use this instead of html `id` attribute.
   */
  @Prop({ mutable: true }) identifier: string;

  /** Label */
  @Prop() label: string;

  /** Show asterisk next to label */
  @Prop() withAsterisk: boolean;

  /** Add type to input field */
  // TODO: handle e, and change name to type
  @Prop() inputType: 'text' | 'email' | 'password' | 'number' | 'search' = 'text';

  /** Input placeholder */
  @Prop() placeholder: string;

  /** Input placeholder */
  @Prop() autocomplete: 'on' | 'off';

  /**
   * If an error occurs, display error
   */
  @Prop() error: boolean;

  /**
   * The helper text displayed below the input field.
   *
   * This text is intended to provide additional guidance or context to users
   * about the input field's purpose, expected values, or any other relevant information.
   * It can be used to clarify the input requirements or to offer tips for entering data.
   *
   * @example
   * <pp-input helperText="Please enter a valid email address."></my-input>
   *
   * @type {string}
   */
  @Prop() helperText: string;

  /**
   * Max character length for input
   */
  @Prop() maxlength: number;

  /**
   * Displays bold text on the input component used in the date picker.
   */
  @Prop() boldInput: boolean;

  /**
   * Optional enter submit
   */
  @Prop() submitOnEnter?: boolean = false;

  private inputElement?: HTMLInputElement;

  @Method()
  async setFocus() {
    this.inputElement?.focus();
  }

  @Listen('keydown')
  // per accessibility guidelines, input component can submit form when user presses enter key
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.submitOnEnter) {
      const formParentOfInput = this.element.closest('form') as HTMLFormElement;
      if (formParentOfInput) {
        formParentOfInput.submit();
      }
    }
  }

  componentDidLoad() {
    if (!this.identifier) {
      this.identifier = 'pp-input-' + this.internalId;
    }
  }

  handleChange(event) {
    this.internals.setFormValue(event.target.value);
    this.value = event.target.value;
    const changeEvent = new Event('change', { bubbles: true });
    this.element.dispatchEvent(changeEvent);
  }

  render() {
    return (
      <Input
        identifier={this.identifier}
        variant={this.variant}
        autocomplete={this.autocomplete}
        onInput={event => this.handleChange(event)}
        boldInput={this.boldInput}
        disabled={this.disabled}
        error={this.error}
        helperText={this.helperText}
        maxlength={this.maxlength}
        inputRef={el => (this.inputElement = el)}
        label={this.label}
        inputType={this.inputType}
        name={this.name}
        placeholder={this.placeholder}
        value={this.value}
        withAsterisk={this.withAsterisk}
      />
    );
  }
}
